/**
 * BlueBean CSV 取込の読み書き(bb_daily_stats / daily_summary / import_logs)と、勤怠の手修正(shinagawa_kintai)。
 *  - bb_daily_stats/{date}_{bb_id}: OPレポート(op_report)と対応履歴(activity[業務コード])を別々に持ち、
 *    どちらを取り込んでも両方を合流させて daily_summary/{date} を作り直す
 *  - 再取込(docs/specs/bluebean-import.md §16): 同じ日・同じハッシュは取込済み扱い。異なるハッシュは確認の上で上書きし、
 *    直前の daily_summary を daily_summary_prev/{date} に退避する。手入力(deliveries・勤怠の手修正)には触らない
 *  - 対応履歴は業務(会社)ごとのファイルで来るので、ファイルに含まれる業務コードの分だけ差し替える
 */
import { Timestamp, collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, where, writeBatch } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import type { ActivitySource, Company, DailySummary, DateStr, Kintai, Operator } from '@core/lib/types';
import { buildDailySummary } from './buildDailySummary';
import { settleCommuteForMonth } from './commuteSettle';
import { fetchMonthSummaries } from '@core/dashboard/dashboardRepo';
import type { ActivityAggregate } from './activity';
import type { ActivityStat, BbDailyStat, DailySummaryBuildResult, ImportFileInfo, ImportIssue, ImportKind, ParsedOpReportRow, UnknownBbIdPolicy } from './types';

export type ImportLog = {
  id: string;
  date: DateStr;
  kind: ImportKind;
  file_name: string;
  hash: string;
  row_count: number;
  staff_count: number;
  warning_count: number;
  previous_hash: string | null;
  business_codes?: string[];
  appointments?: number;
  by: string;
  at?: unknown;
};

/** その日の取込履歴(新しい順) */
export async function fetchImportLogs(date: DateStr, kind: ImportKind): Promise<ImportLog[]> {
  const snap = await getDocs(query(collection(getDb(), 'import_logs'), where('date', '==', date), where('kind', '==', kind), orderBy('at', 'desc')));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as ImportLog);
}

export async function fetchExistingSummary(date: DateStr): Promise<DailySummary | null> {
  const snap = await getDoc(doc(getDb(), 'daily_summary', date));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as object) } as DailySummary) : null;
}

/** その日の bb_daily_stats(bb_id → 内容)。旧形式(OPレポートの値を平置き)も読めるようにする */
export async function fetchBbDailyStats(date: DateStr): Promise<Map<string, BbDailyStat>> {
  const snap = await getDocs(query(collection(getDb(), 'bb_daily_stats'), where('date', '==', date)));
  const out = new Map<string, BbDailyStat>();
  for (const d of snap.docs) {
    const raw = d.data() as Record<string, unknown>;
    let stat: BbDailyStat;
    if (!raw.op_report && raw.kind === 'op_report' && typeof raw.bb_id === 'string') {
      const { date: _d, kind: _k, updated_at: _u, import_id, ...rest } = raw;
      stat = { date, bb_id: raw.bb_id, op_report: { ...(rest as ParsedOpReportRow), import_id: String(import_id ?? ''), file_name: '' } };
    } else {
      stat = raw as BbDailyStat;
    }
    if (stat.bb_id) out.set(stat.bb_id, stat);
  }
  return out;
}

/** その日の勤怠の手修正(bb_id → 勤怠) */
export async function fetchKintai(date: DateStr): Promise<Map<string, Kintai>> {
  const snap = await getDocs(query(collection(getDb(), 'shinagawa_kintai'), where('date', '==', date)));
  const out = new Map<string, Kintai>();
  for (const d of snap.docs) {
    const k = d.data() as Kintai;
    if (k.bb_id) out.set(k.bb_id, k);
  }
  return out;
}

/** bb_daily_stats → buildDailySummary の入力 */
export function statsToBuildInput(stats: Map<string, BbDailyStat>): { opRows: ParsedOpReportRow[]; activity: Map<string, Map<string, ActivityStat>> } {
  const opRows: ParsedOpReportRow[] = [];
  const activity = new Map<string, Map<string, ActivityStat>>();
  for (const [bbId, s] of stats) {
    if (s.op_report) {
      const { import_id: _i, file_name: _f, ...row } = s.op_report;
      opRows.push(row);
    }
    if (s.activity && Object.keys(s.activity).length > 0) {
      const m = new Map<string, ActivityStat>();
      for (const [code, a] of Object.entries(s.activity)) {
        const { import_id: _i, file_name: _f, ...stat } = a;
        m.set(code, stat);
      }
      activity.set(bbId, m);
    }
  }
  return { opRows, activity };
}

/** OPレポートを取り込んだ後の bb_daily_stats(メモリ上で合成) */
export function mergeOpReport(stats: Map<string, BbDailyStat>, date: DateStr, rows: ParsedOpReportRow[], importId: string, fileName: string): Map<string, BbDailyStat> {
  const next = new Map<string, BbDailyStat>();
  // OPレポートは1日1ファイルが全体なので、ファイルに無い BB ID の op_report は消す
  for (const [bbId, s] of stats) {
    const { op_report: _o, ...rest } = s;
    next.set(bbId, { ...rest, date, bb_id: bbId });
  }
  for (const r of rows) {
    const prev = next.get(r.bb_id) ?? { date, bb_id: r.bb_id };
    next.set(r.bb_id, { ...prev, op_report: { ...r, import_id: importId, file_name: fileName } });
  }
  return next;
}

/** 対応履歴を取り込んだ後の bb_daily_stats(メモリ上で合成)。ファイルに含まれる業務コードの分だけ差し替える */
export function mergeActivity(stats: Map<string, BbDailyStat>, date: DateStr, agg: ActivityAggregate, importId: string, fileName: string): Map<string, BbDailyStat> {
  const codes = new Set(agg.business_codes);
  const next = new Map<string, BbDailyStat>();
  for (const [bbId, s] of stats) {
    const kept = Object.fromEntries(Object.entries(s.activity ?? {}).filter(([code]) => !codes.has(code)));
    next.set(bbId, { ...s, date, bb_id: bbId, activity: kept });
  }
  for (const [bbId, perCode] of agg.stats) {
    const prev = next.get(bbId) ?? { date, bb_id: bbId, activity: {} };
    const activity = { ...(prev.activity ?? {}) };
    for (const [code, stat] of perCode) activity[code] = { ...stat, import_id: importId, file_name: fileName };
    next.set(bbId, { ...prev, activity });
  }
  return next;
}

function isEmptyStat(s: BbDailyStat): boolean {
  return !s.op_report && Object.keys(s.activity ?? {}).length === 0;
}

type CommonArgs = {
  date: DateStr;
  file: ImportFileInfo;
  operators: Operator[];
  companies: Company[];
  policy: UnknownBbIdPolicy;
  issues: ImportIssue[];
  previousHash: string | null;
  existing: DailySummary | null;
  /** その日の勤怠の手修正(無ければ空) */
  kintai?: Map<string, Kintai>;
  userId: string;
};

/** 合成済みの bb_daily_stats から daily_summary を作り直して、まとめて書く(失敗時は何も残さない) */
async function commitImport(
  args: CommonArgs & {
    beforeStats: Map<string, BbDailyStat>;
    afterStats: Map<string, BbDailyStat>;
    sources: NonNullable<DailySummary['sources']>;
    logExtra: Record<string, unknown>;
    auditAction: string;
    auditText: (build: DailySummaryBuildResult) => string;
    /** 勤怠の確認済み(kintai_check)を引き継ぐか。OPレポートの取込は稼働が変わるので引き継がない(再確認)、対応履歴の取込は引き継ぐ */
    keepKintaiCheck: boolean;
  },
): Promise<{ importId: string; build: DailySummaryBuildResult }> {
  const { date, file, operators, companies, policy, issues, previousHash, existing, userId, beforeStats, afterStats, sources, logExtra } = args;
  const db = getDb();
  const logRef = doc(collection(db, 'import_logs'));
  const { opRows, activity } = statsToBuildInput(afterStats);
  const build = buildDailySummary({ date, importId: logRef.id, opRows, activity, kintai: args.kintai, operators, companies, policy });
  if (build.issues.some((i) => i.level === 'error')) {
    throw new Error(build.issues.filter((i) => i.level === 'error').map((i) => i.message).join(' / '));
  }
  const batch = writeBatch(db);
  if (existing) {
    const { id: _id, ...prev } = existing;
    batch.set(doc(db, 'daily_summary_prev', date), { ...prev, replaced_at: serverTimestamp(), replaced_by: userId, replaced_import: logRef.id });
  }
  const { id: _sid, ...summaryData } = build.summary;
  batch.set(doc(db, 'daily_summary', date), {
    ...summaryData,
    sources,
    ...(args.keepKintaiCheck && existing?.kintai_check ? { kintai_check: existing.kintai_check } : {}),
    updated_by: userId,
    updated_at: serverTimestamp(),
    ...(existing?.created_at ? {} : { created_at: serverTimestamp() }),
  });
  for (const [bbId, s] of afterStats) {
    const ref = doc(db, 'bb_daily_stats', `${date}_${bbId}`);
    if (isEmptyStat(s)) batch.delete(ref);
    else batch.set(ref, { ...s, updated_at: serverTimestamp() });
  }
  for (const bbId of beforeStats.keys()) if (!afterStats.has(bbId)) batch.delete(doc(db, 'bb_daily_stats', `${date}_${bbId}`));
  batch.set(logRef, {
    date,
    kind: file.kind,
    file_name: file.name,
    hash: file.hash,
    row_count: file.rowCount,
    staff_count: build.summary.staff.length,
    warning_count: [...issues, ...build.issues].filter((i) => i.level === 'warning').length,
    previous_hash: previousHash,
    ...logExtra,
    by: userId,
    at: serverTimestamp(),
  });
  await batch.commit();
  await settleCommute(date, userId);
  await writeAuditLog({ targetType: 'import', targetId: `daily_summary/${date}`, action: args.auditAction, by: userId, changes: [args.auditText(build)] });
  return { importId: logRef.id, build };
}

/**
 * その日を取り込んだあと、**その月の交通費を計算し直す**(P-38: 月の実稼働100時間以上なら全日70%)。
 * 変わった日だけ書き戻す。取込・勤怠修正のあとに必ず通す(画面の描画からは呼ばない)
 */
async function settleCommute(date: DateStr, userId: string): Promise<void> {
  const month = date.slice(0, 7);
  const days = await fetchMonthSummaries(month);
  const changed = settleCommuteForMonth(days.map((d) => ({ id: d.id, staff: d.staff ?? [] })));
  if (changed.length === 0) return;
  const db = getDb();
  const batch = writeBatch(db);
  for (const c of changed) batch.update(doc(db, 'daily_summary', c.id), { staff: c.staff, updated_by: userId, updated_at: serverTimestamp() });
  await batch.commit();
}

export async function runOpReportImport(args: CommonArgs & { rows: ParsedOpReportRow[]; stats: Map<string, BbDailyStat> }): Promise<{ importId: string; build: DailySummaryBuildResult }> {
  const { date, file, rows, stats, existing } = args;
  const importId = doc(collection(getDb(), 'import_logs')).id;
  const afterStats = mergeOpReport(stats, date, rows, importId, file.name);
  const sources: NonNullable<DailySummary['sources']> = {
    ...(existing?.sources ?? {}),
    op_report: { import_id: importId, file_name: file.name, hash: file.hash, row_count: file.rowCount, imported_at: serverTimestamp() },
  };
  return commitImport({
    ...args,
    beforeStats: stats,
    afterStats,
    sources,
    logExtra: {},
    auditAction: 'op-report-import',
    auditText: (b) => `OPレポート ${file.name}(${file.rowCount}行) を ${date} に取込。スタッフ ${b.summary.staff.length}行${args.previousHash ? '(上書き)' : ''}`,
    keepKintaiCheck: false,
  });
}

export async function runActivityImport(args: CommonArgs & { agg: ActivityAggregate; stats: Map<string, BbDailyStat> }): Promise<{ importId: string; build: DailySummaryBuildResult }> {
  const { date, file, agg, stats, existing } = args;
  const importId = doc(collection(getDb(), 'import_logs')).id;
  const afterStats = mergeActivity(stats, date, agg, importId, file.name);
  const codes = new Set(agg.business_codes);
  const keptSources = (existing?.sources?.activity ?? []).filter((s) => !s.business_codes.some((c) => codes.has(c)));
  const entry: ActivitySource = {
    import_id: importId,
    file_name: file.name,
    hash: file.hash,
    row_count: file.rowCount,
    business_codes: agg.business_codes,
    appointments: agg.totals.appointments,
    // 配列の中では serverTimestamp() が使えないため端末時刻
    imported_at: Timestamp.now(),
  };
  const sources: NonNullable<DailySummary['sources']> = { ...(existing?.sources ?? {}), activity: [...keptSources, entry] };
  return commitImport({
    ...args,
    beforeStats: stats,
    afterStats,
    sources,
    logExtra: { business_codes: agg.business_codes, appointments: agg.totals.appointments },
    auditAction: 'activity-import',
    auditText: (b) =>
      `対応履歴 ${file.name}(${file.rowCount}行・業務 ${agg.business_codes.join('/') || 'なし'}) を ${date} に取込。取得 ${agg.totals.appointments}件、スタッフ ${b.summary.staff.length}行${keptSources.length !== (existing?.sources?.activity ?? []).length ? '(同じ業務を差し替え)' : ''}`,
    keepKintaiCheck: true,
  });
}

/** 勤怠の確認で変える1件。value = null は手修正を消して自動値に戻す */
export type KintaiEntry = { bb_id: string; op_id: string; value: { shift_hours: number; break_hours: number; note: string } | null };

/**
 * 出勤(品川・タイミー)の勤怠の確認(D-56)。変更分(entries)を shinagawa_kintai に書き、
 * その日の daily_summary を bb_daily_stats と勤怠から作り直して「確認済み」(kintai_check)を付ける。
 * 取込元の記録(sources)はそのまま。1バッチで書き、途中失敗では何も残さない
 */
export async function saveKintaiCheck(args: { date: DateStr; entries: KintaiEntry[]; operators: Operator[]; companies: Company[]; userId: string; userName: string | null }): Promise<void> {
  const { date, entries, operators, companies, userId, userName } = args;
  const db = getDb();
  const [stats, existing, current] = await Promise.all([fetchBbDailyStats(date), fetchExistingSummary(date), fetchKintai(date)]);
  if (stats.size === 0) throw new Error('この日は BlueBean の取込データがありません(勤怠の確認は OPレポートの取込後)');
  const kintai = new Map(current);
  const batch = writeBatch(db);
  const changes: string[] = [];
  for (const e of entries) {
    const ref = doc(db, 'shinagawa_kintai', `${date}_${e.bb_id}`);
    if (e.value) {
      const k: Kintai = { date, bb_id: e.bb_id, op_id: e.op_id, shift_hours: e.value.shift_hours, break_hours: e.value.break_hours, note: e.value.note.trim() || null };
      batch.set(ref, { ...k, updated_by: userId, updated_at: serverTimestamp() });
      kintai.set(e.bb_id, k);
      changes.push(`${e.bb_id}: 拘束 ${k.shift_hours}h・休憩 ${k.break_hours}h に手修正${k.note ? `(${k.note})` : ''}`);
    } else {
      batch.delete(ref);
      kintai.delete(e.bb_id);
      changes.push(`${e.bb_id}: 手修正を取り消し(自動値に戻す)`);
    }
  }
  const { opRows, activity } = statsToBuildInput(stats);
  const build = buildDailySummary({ date, importId: existing?.sources?.op_report?.import_id ?? 'kintai', opRows, activity, kintai, operators, companies, policy: 'exclude_rows' });
  const { id: _sid, ...summaryData } = build.summary;
  batch.set(doc(db, 'daily_summary', date), {
    ...summaryData,
    sources: existing?.sources ?? {},
    kintai_check: { at: serverTimestamp(), by: userId, by_name: userName },
    updated_by: userId,
    updated_at: serverTimestamp(),
    created_at: existing?.created_at ?? serverTimestamp(),
  });
  await batch.commit();
  await settleCommute(date, userId); // 勤怠を直すと実稼働が変わる → 交通費(P-38)も計算し直す
  await writeAuditLog({
    targetType: 'import',
    targetId: `daily_summary/${date}`,
    action: 'kintai-check',
    by: userId,
    changes: [`${date} の出勤の勤怠を確認済みにした(変更 ${entries.length}件)`, ...changes],
  });
}
